import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import {
    Box,
    Button,
    IconButton,
    LinearProgress,
    AppBar as MuiAppBar,
    AppBarProps as MuiAppBarProps,
    Toolbar,
    Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import React from "react";
import { Route, Switch } from "react-router-dom";
import { drawerWidth } from ".";
import { getSessionCookie, isLoggedIn } from "../components/cookies";
import * as loginService from "../services/login/login";
import { commonSlice, commonState, store } from "../store";
import { colors, fontSizes } from "../theme";

const HeaderTitle: React.FC = () => {
    return (
        <Switch>
            <Route exact path="/"></Route>
            <Route path="/login">ログイン</Route>
            <Route path="/login_otp">ログイン</Route>
            <Route path="/driver_trips">ナビ利用詳細</Route>
            <Route path="/trips_map">ナビ利用詳細</Route>
            <Route path="/driver_breaks">停車詳細</Route>
            <Route path="/shift_monitoring">乗務検索</Route>
            <Route path="/shift_search">乗務検索</Route>
            <Route path="/payments">成果型課金：請求金額確認</Route>
            <Route path="/driver_paid_trips">成果型課金：利用実績照会</Route>
            <Route path="/driver_usage">成果型課金：利用実績照会</Route>
            <Route exact path="/capacity">
                成果型課金：課金上限設定
            </Route>
            <Route path="/drivers_list_menu">契約乗務員一覧</Route>
            <Route exact path="/release_note">
                リリースノート
            </Route>
            <Route exact path="/weekly_report">
                週次レポート
            </Route>
            <Route exact path="/manual">
                マニュアル
            </Route>
        </Switch>
    );
};

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open",
})<MuiAppBarProps & { open: boolean }>(({ theme, open }) => ({
    backgroundColor: colors.black.main,
    padding: 0,
    left: 0,
    minHeight: 80,
    transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const TitleHeader: React.FC<commonState> = (props: commonState) => {
    let versionTag = "";
    if (["mpu", "dev"].includes(props.versions.build)) {
        versionTag = props.versions.build;
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "flex-start",
            }}
        >
            <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={() => {
                    store.dispatch(
                        commonSlice.actions.toggleLeftMenuCollapsedState()
                    );
                }}
                edge="start"
                sx={{
                    mt: 4,
                    mb: 4,
                    mr: 2,
                    ml: 2,
                    p: 3,
                    color: colors.white.main,
                    "&:hover": {
                        backgroundColor: colors.gray.a10,
                    },
                    ...(props.navigation.leftNavMenuIsCollapsed && {
                        display: "none",
                    }),
                }}
            >
                <MenuOutlinedIcon sx={{ fontSize: fontSizes.xxlarge }} />
            </IconButton>
            <Box
                sx={{
                    m: 2,
                    display: "flex",
                    justifyContent: "flex-start",
                }}
            >
                <Box>
                    <Typography
                        variant="h1"
                        sx={{
                            color: "common.white",
                            fontSize: fontSizes.xlarge,
                            fontWeight: "bold",
                            lineHeight: "64px",
                        }}
                    >
                        <HeaderTitle />
                    </Typography>
                </Box>
                <Box
                    sx={{
                        color: "darkseagreen",
                        ml: 8,
                        lineHeight: "64px",
                        fontSize: fontSizes.xlarge,
                        fontWeight: "bold",
                    }}
                >
                    <em>{versionTag}</em>
                </Box>
            </Box>
        </Box>
    );
};

const OfficeName: React.FC<commonState> = (props: commonState) => {
    if (!isLoggedIn()) {
        return <></>;
    }
    return (
        <Box sx={{ mr: 8, lineHeight: "64px" }}>
            {getSessionCookie().consoleUserName}
        </Box>
    );
};

const LogoutButton: React.FC<commonState> = (props: commonState) => {
    if (!isLoggedIn()) {
        return <></>;
    }
    async function click() {
        loginService.Logout();
    }

    return (
        <Button variant="text" onClick={click} sx={{ mt: 3 }}>
            ログアウト
        </Button>
    );
};

const ProgressBar: React.FC<commonState> = (props: commonState) => {
    if (!props.loading) {
        return <></>;
    }
    return <LinearProgress sx={{ height: 2 }} />;
};

export const Header: React.FC<commonState> = (props: commonState) => {
    if (props) {
        const theme = useTheme();

        return (
            <AppBar
                position="fixed"
                theme={theme}
                open={props.navigation.leftNavMenuIsCollapsed}
            >
                <Toolbar>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                        }}
                    >
                        <Box>
                            <TitleHeader {...props} />
                        </Box>
                        <Box
                            sx={{
                                m: 2,
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <OfficeName {...props} />
                            <LogoutButton {...props} />
                        </Box>
                    </Box>
                </Toolbar>
                <ProgressBar {...props} />
            </AppBar>
        );
    }
    return <></>;
};
